import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Grid, Stack, Typography } from '@mui/material';
import { Accordion } from 'components/molecules/Accordion';
import { useTranslations } from 'hooks/useTranslations';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { useAncillaries } from 'pages/AncillariesPage/hooks';
import { EDictionaryTypes } from 'models/dictionary';
import { useLang } from 'models/langContext';
import { ERouteLinks } from 'models/route';
import { useAlert } from 'models/alertContext';
import { Alert, EAlertVariants, IAlert } from 'components/atoms/Alert';
import { DetailsTemplate, EDetailsPageTypes } from 'components/templates/DetailsTemplate';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import Button from 'components/atoms/Button';
import { Chips } from 'components/atoms/Chips';
import { getFieldsByAmmunitionType } from 'pages/AncillariesPage/helpers';
import {
  EAncillaryState,
  EArtAmmunitionType,
  EArtAncillaryTypeName,
  EArtAncillaryTypeNameLowerCase,
} from 'constants/ArtAccessoryType';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { clearAlertTimeout, EAlertTime, formatDate } from 'utils/date';
import { IAncillaryDetailsCustomfield } from 'models/ancillaries';
import { ECustomFieldObjectType, ECustomFieldType } from 'models/customField';
import { EPermission } from 'models/permissions';
import { usePermission } from 'hooks/usePermission';
import Link from '@mui/material/Link';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { ActionTypes } from 'state/actions/alert';
import { EFirearmStateId } from 'models/firearm';
import { renderDataColorByValues } from 'components/molecules/Table/helpers';
import { useLegalEntities } from 'pages/LegalEntitiesPage/hooks';
import { StyledLegalLink } from 'components/atoms/StyledLegalLink';
import { getLegalEntityType } from 'components/organisms/RegisteredFirearmDetails';
import { usePrivilegesValidation } from 'utils/privilegesValidations';
import { Asset } from 'utils/assets';
import { EState, statesWithoutMovedTransaction } from 'utils/transactionUtils';
import { AmmunitionInformationContainer, AmmunitionInformationTitle } from './styles';
import { AmmunitionTransactions } from './AmmunitionTransactions';
import { AmmunitionComments } from './AmmunitionComments';
import { AmmunitionAttachments } from './AmmunitionAttachments';
import ImagesGallery from 'components/organisms/ImagesGallery';
import { EModuleType } from 'components/organisms/ImagesGallery/helper';
import ImagesGalleryRadioButton from 'components/organisms/ImagesGalleryRadioButton';
import { EGalleryTypeAncillary } from 'components/organisms/ImagesGalleryRadioButton/helpers';
import { EMarkingTypes } from 'constants/Markings';
import { findInDictionary } from 'helpers/dictionary';

export enum EAmmunitionLabels {
  ammunitionGeneralInformation = 'ammunitionDetails.ammunitionGeneralInformation.header',
  ammunitionMarkings = 'ammunitionDetails.ammunitionMarkings.header',
  showArtInformation = 'ammunitionDetails.showArtInformation.button',
  ammunitionType = 'ammunitionDetails.ammunitionType.label',
  artId = 'ammunitionDetails.artId.label',
  ancillaryType = 'ancillaryDetails.ancillaryType.label',
  ammunitionManufacturer = 'ammunitionDetails.ammunitionManufacturer.label',
  productName = 'ammunitionDetails.productName.label',
  factory = 'ammunitionDetails.factory.label',
  calibreGauge = 'ammunitionDetails.CalibreGauge.label',
  functionalType = 'ammunitionDetails.functionalType.label',
  cartridgeComposition = 'ammunitionDetails.cartrigeComposition.label',
  colourOfPrimerSealant = 'ammunitionDetails.colourOfPrimerSealant.label',
  projectileShape = 'ammunitionDetails.projectileShape.label',
  headstampYear = 'ammunitionDetails.headstampYear.label',
  alphanumericalMarkins = 'ammunitionDetails.alphanumericalMarkins.label',
  expirationDate = 'ammunitionDetails.expirationDate.label',
  productionDate = 'ammunitionDetails.manufacturedDate.label',
  munitionVelocity = 'ammunitionDetails.munitionVelocity.label',
  shotSize = 'ammunitionDetails.shotSize.label',
  munitionType = 'ammunitionDetails.munitionType.label',
  ammunitionDetailedInformation = 'ammunitionDetails.ammunitionDetailedInformation.header',
  firecycleID = 'ammunitionDetails.firecycleID.label',
  lotNumber = 'ammunitionDetails.lotNumber.label',
  batchNumber = 'ammunitionDetails.batchNumber.label',
  assetId = 'ammunitionDetails.assetId.label',
  ammunitionState = 'ammunitionDetails.ammunitionState.label',
  currentOwner = 'ammunitionDetails.currentOwner.label',
  currentKeeper = 'ammunitionDetails.currentKeeper.label',
  manufacturedAt = 'ammunitionDetails.accordion.manufacturedAt.label',
  expiryDate = 'ammunitionDetails.expiryDate.label',
  exhibitNumber = 'ammunitionDetails.exhibitNumber.label',

  importMarking = 'ammunitionDetails.importMarking.label',
  exportMarking = 'ammunitionDetails.exportMarking.label',
  deactivationMarking = 'ammunitionDetails.deactivationMarking.label',
  confiscationMarking = 'ammunitionDetails.confiscationMarking.label',
  surplusSaleMarking = 'ammunitionDetails.surplusSaleMarking.label',
  appliedMarking = 'ammunitionDetails.appliedMarking.label',
  marking = 'transactionDetails.ancillaryDetails.accordion.marking.label',
  yearOfTransfer = 'transactionDetails.ancillaryDetails.accordion.yearOfTransfer.label',
  yearOfConfiscation = 'transactionDetails.ancillaryDetails.accordion.yearOfConfiscation.label',
}

const DetailAmmunition = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslations();
  const { selectedLanguage, defaultLanguage } = useLang();
  const navigate = useNavigate();
  const { nextPageAlert, clearAlert, transactionAlert, ancillaryAlert, setAlert } = useAlert();
  const {
    loadDictionaries,
    ammunitionDetail,
    getAmmunitionDetail,
    artAmmunitionType,
    artAmmunitionManufacturer,
    artAmmunitionCalibreGauge,
    state,
  } = useAncillaries();
  const { shortDateFormat } = useGlobalProperty();
  const [accessFields, setAccessFields] = useState<string[]>([]);
  const [isAllExpanded, setIsAllExpanded] = useState<boolean>(false);
  const { hasPermissions } = usePermission();
  const { getCustomField, customFieldsAncillaries } = useLegalEntities();
  const { isAnyLicenceAuthorityValid } = usePrivilegesValidation();
  const [galleryType, setGalleryType] = useState<EGalleryTypeAncillary>(
    EGalleryTypeAncillary.ART,
  );
  const [showMarkings, setShowMarkings] = useState(false);

  useEffect(() => {
    if (ammunitionDetail) {
      if (ammunitionDetail.typeUniqueId) {
        const fields = getFieldsByAmmunitionType(
          ammunitionDetail.typeUniqueId as EArtAmmunitionType,
        );
        setAccessFields(fields);
      } else {
        setAccessFields([]);
      }
    }
  }, [ammunitionDetail]);

  useEffect(() => {
    (async () => {
      if (id) {
        await getAmmunitionDetail(id);
      }
    })();
  }, [id]);

  useEffect(() => {
    (async () => {
      await getCustomField(ECustomFieldObjectType.AN_AMMUNITION);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () => {
      clearAlertTimeout(clearAlert, ActionTypes.CLEAR_SAME_PAGE_ALERT, EAlertTime.SHORT);
      clearAlertTimeout(clearAlert, ActionTypes.CLEAR_NEXT_PAGE_ALERT, EAlertTime.SHORT);
      clearAlertTimeout(clearAlert, ActionTypes.CLEAR_TRANSACTION_ALERT, EAlertTime.SHORT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_ANCILLARY_ALERT);
    },
    [],
  );

  useEffect(() => {
    (async function initDictionaries() {
      await loadDictionaries([
        EDictionaryTypes.ArtAncillaryOptions,
        EDictionaryTypes.ArtAmmunitionType,
        EDictionaryTypes.ArtAmmunitionCalibreGauge,
        EDictionaryTypes.ArtAmmunitionManufacturer,
        EDictionaryTypes.State,
      ]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const showField = (fieldName: string) => accessFields.includes(fieldName);

  const licenceAuthorityValid =
    !!ammunitionDetail && isAnyLicenceAuthorityValid(ammunitionDetail.licenceAuthorityIds);

  const renderCustomFieldData = (customField: IAncillaryDetailsCustomfield) => {
    if (customField.customFieldType === ECustomFieldType.BOOL) {
      if (customField.value === null) {
        return '-';
      }
      return customField.value
        ? t('customFields.value.yes.label')
        : t('customFields.value.no.label');
    }
    if (customField.customFieldType === ECustomFieldType.DATE) {
      return formatDate(customField.value, shortDateFormat);
    }
    return customField.value || '-';
  };

  const navigateToMap = () => {
    navigate(ERouteLinks.RegisteredAncillariesMap, {
      state: {
        ancillaryUniqueId: ammunitionDetail?.uniqueId,
        ancillaryType: EArtAncillaryTypeName.AMMUNITION.toLowerCase(),
        legalityUniqueId: ammunitionDetail?.legalityUniqueId,
        navigateBack: `${ERouteLinks.ViewAmmunition.replace(':id', id as string)}`,
        showAdvanced: true,
        showLegalityBar: false,
        breadcrumbs: [
          {
            label: t('module.ancillaries.name'),
            route: ERouteLinks.Ancillaries,
          },
          {
            label: t('module.ammunition.name'),
            route: `${ERouteLinks.Ancillaries}?expand=ammunition`,
          },
          {
            label: t('ammunitionDetails.accordion.title.label'),
            route: `${ERouteLinks.ViewAmmunition.replace(':id', id as string)}`,
          },
          t('ancillaries.ancillariesLocation.showOnMap.label'),
        ],
      },
    });
  };

  const handleRedirectToEditPageButton = (): (() => void) => {
    if (
      customFieldsAncillaries?.length &&
      ammunitionDetail?.stateUniqueId !== EFirearmStateId.DISABLED
    ) {
      return () =>
        navigate(
          ERouteLinks.EditAncillary.replace(
            ':id',
            ammunitionDetail?.uniqueId as string,
          ).replace(':type', 'ammunition'),
        );
    }
    if (ammunitionDetail?.stateUniqueId === EFirearmStateId.DISABLED) {
      return () =>
        setAlert(ActionTypes.SET_ANCILLARY_ALERT, {
          text: t('ancillary.disabled.edit.info'),
          variant: EAlertVariants.error,
        } as IAlert);
    }
    return () =>
      setAlert(ActionTypes.SET_ANCILLARY_ALERT, {
        text: t('general.noAvaibleDataToEdit.label'),
        variant: EAlertVariants.warning,
      } as IAlert);
  };

  const handleRedirectToMovedTransaction = () => {
    if (statesWithoutMovedTransaction.includes(ammunitionDetail?.stateUniqueId as EState))
      return;

    if (ammunitionDetail?.groupName) {
      setAlert(ActionTypes.SET_TRANSACTION_ALERT, {
        text: t('ancillary.transaction.addTransaction.text'),
        variant: EAlertVariants.warning,
      } as IAlert);
      clearAlert(ActionTypes.SET_SAME_PAGE_ALERT);
      return;
    }

    navigate(ERouteLinks.MoveTransaction, {
      state: {
        assetUniqueId: ammunitionDetail?.uniqueId,
        asset: Asset.ANCILLARY,
        isGroup: false,
        searchAddressesFor: ammunitionDetail?.keeperId || ammunitionDetail?.ownerId,
        navigateBack: `${ERouteLinks.ViewAmmunition.replace(':id', id as string)}`,
        breadcrumbs: [
          {
            label: t('module.ancillaries.name'),
            route: ERouteLinks.Ancillaries,
          },
          {
            label: t('module.ammunition.name'),
            route: `${ERouteLinks.Ancillaries}?expand=ammunition`,
          },
          {
            label: t('ammunitionDetails.accordion.title.label'),
            route: `${ERouteLinks.ViewAmmunition.replace(':id', id as string)}`,
          },
          t('transactions.movedTransaction.breadcrumb.label'),
        ],
      },
    });
  };

  return (
    <div>
      <Breadcrumbs
        items={[
          {
            label: t('module.ancillaries.name'),
            route: ERouteLinks.Ancillaries,
          },
          {
            label: t('module.ammunition.name'),
            route: `${ERouteLinks.Ancillaries}?expand=ammunition`,
          },
          t('ammunitionDetails.accordion.title.label'),
        ]}
      />
      {ammunitionDetail && accessFields.length && (
        <Grid sx={{ gap: 3 }}>
          <DetailsTemplate
            title={findInDictionary(ammunitionDetail?.typeUniqueId, artAmmunitionType)}
            pageType={EDetailsPageTypes.view}
            redirectToShowOnMap={
              ![
                EAncillaryState.DESTROYED,
                EAncillaryState.DEPLETED,
                EAncillaryState.DISABLED,
              ].includes(ammunitionDetail.stateUniqueId as EAncillaryState) &&
              ammunitionDetail?.latitude
                ? navigateToMap
                : undefined
            }
            showOnMapButtonId="ammunition-show-on-map-button"
            editPermissions={[EPermission.MANAGE_LE]}
            redirectToEditView={
              licenceAuthorityValid && ammunitionDetail.customFields
                ? handleRedirectToEditPageButton()
                : undefined
            }
            redirectToDisableView={
              ammunitionDetail.stateUniqueId === EAncillaryState.DISABLED ||
              !hasPermissions([EPermission.DISABLE_ANCILLARY]) ||
              !licenceAuthorityValid
                ? undefined
                : () =>
                    navigate(
                      `${ERouteLinks.DisableAncillary.replace(
                        ':id',
                        ammunitionDetail.uniqueId,
                      ).replace(':type', EArtAncillaryTypeName.AMMUNITION.toLowerCase())}`,
                    )
            }
            redirectToMovedTransaction={handleRedirectToMovedTransaction}
            hideMovedTransaction={
              !licenceAuthorityValid ||
              statesWithoutMovedTransaction.includes(
                ammunitionDetail?.stateUniqueId as EState,
              ) ||
              !hasPermissions(EPermission.ADD_TRANSACTION)
            }
            showEditButton={customFieldsAncillaries.length > 0}
          >
            <Grid
              container
              spacing="20px"
              sx={{ flexWrap: { xs: 'wrap', md: 'nowrap' }, justifyContent: 'center' }}
            >
              <Grid container sx={{ gap: '12px', alignContent: 'flex-start', width: '270px' }}>
                <ImagesGalleryRadioButton
                  setGalleryType={setGalleryType}
                  galleryType={galleryType}
                  moduleType={EModuleType.ANCILLARY}
                />
                <ImagesGallery
                  key={'ancillary_details_gallery'}
                  moduleType={EModuleType.ANCILLARY}
                  ownerId={ammunitionDetail.uniqueId}
                  buttonId={'ancillary_details-add_photo'}
                  buttonPermission={EPermission.EDIT_ANCILLARY}
                  hidden={galleryType !== EGalleryTypeAncillary.Owner}
                  init={() => setGalleryType(EGalleryTypeAncillary.Owner)}
                  onClickEdit={() =>
                    navigate(
                      `${ERouteLinks.AmmunitionEditImages.replace(
                        ':id',
                        ammunitionDetail.uniqueId as string,
                      )}`,
                    )
                  }
                  onClickAdd={() =>
                    navigate(
                      `${ERouteLinks.AmmunitionAddImages.replace(
                        ':id',
                        ammunitionDetail.uniqueId as string,
                      )}`,
                    )
                  }
                />
                <ImagesGallery
                  key={'ancillary_details_ART_gallery'}
                  moduleType={EModuleType.ART}
                  ownerId={ammunitionDetail.artUniqueId}
                  buttonId={'ancillary_details_ART-add_photo'}
                  buttonPermission={EPermission.MANAGE_LOCAL_ART}
                  hidden={galleryType !== EGalleryTypeAncillary.ART}
                  showButton={false}
                />
              </Grid>
              <Grid
                sx={{
                  width: { xs: '100%', md: 'calc(100% - 270px)' },
                  rowGap: 1,
                  alignContent: 'flex-start',
                }}
              >
                <AmmunitionInformationContainer size={{ xs: 12 }} container>
                  <Grid size={{ xs: 12 }}>
                    <Stack>
                      <AmmunitionInformationTitle variant="subtitle1">
                        {t(EAmmunitionLabels.ammunitionGeneralInformation)}
                      </AmmunitionInformationTitle>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAmmunitionLabels.ammunitionType)}
                      </Typography>
                      <Typography variant="body2">
                        {findInDictionary(ammunitionDetail?.typeUniqueId, artAmmunitionType)}
                      </Typography>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography
                        variant="subtitle2"
                        sx={{ marginBottom: '4px', textTransform: 'uppercase' }}
                      >
                        {t(EAmmunitionLabels.artId)}
                      </Typography>
                      <Typography variant="body2" style={{ textTransform: 'capitalize' }}>
                        {emptyFieldHolder(ammunitionDetail?.artId)}
                      </Typography>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, lg: 6 }}>
                    <Stack>
                      {ammunitionDetail.artUniqueId && (
                        <Link
                          href={ERouteLinks.ViewAncillaryDetails.replace(
                            ':id',
                            ammunitionDetail.artUniqueId,
                          ).replace(':type', EArtAncillaryTypeNameLowerCase.AMMUNITION)}
                          underline="none"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button
                            variant={EButtonVariants.contained}
                            size={EButtonSizes.small}
                            label={t(EAmmunitionLabels.showArtInformation)}
                            fullWidth
                          />
                        </Link>
                      )}
                    </Stack>
                  </Grid>
                  {showField('manufacturer') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAmmunitionLabels.ammunitionManufacturer)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(
                            findInDictionary(
                              ammunitionDetail?.manufacturerUniqueId,
                              artAmmunitionManufacturer,
                            ),
                          )}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  {showField('productName') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAmmunitionLabels.productName)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(ammunitionDetail?.productName)}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  {showField('factory') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAmmunitionLabels.factory)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(ammunitionDetail?.factory)}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  {showField('calibreGauge') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAmmunitionLabels.calibreGauge)}
                        </Typography>
                        <Typography variant="body2">
                          {findInDictionary(
                            ammunitionDetail?.calibreUniqueId,
                            artAmmunitionCalibreGauge,
                          )}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  {showField('cartridgeComposition') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAmmunitionLabels.cartridgeComposition)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(ammunitionDetail?.cartridgeComposition)}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  {showField('colourOfPrimerSealant') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAmmunitionLabels.colourOfPrimerSealant)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(ammunitionDetail?.colourOfPrimerSealant)}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  {showField('shotSize') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAmmunitionLabels.shotSize)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(ammunitionDetail?.shotSize)}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  {showField('munitionVelocity') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAmmunitionLabels.munitionVelocity)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(ammunitionDetail?.munitionVelocity)}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  {showField('functionalType') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAmmunitionLabels.functionalType)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(ammunitionDetail?.functionalType)}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  {showField('munitionType') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAmmunitionLabels.munitionType)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(ammunitionDetail?.munitionType)}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  {showField('projectileShape') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAmmunitionLabels.projectileShape)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(ammunitionDetail?.projectileShape)}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  {showField('alphanumericalMarkins') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAmmunitionLabels.alphanumericalMarkins)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(ammunitionDetail?.alphanumericalMarkins)}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                </AmmunitionInformationContainer>
                <AmmunitionInformationContainer
                  size={{ xs: 12 }}
                  container
                  borderHidden={!showMarkings}
                >
                  <Grid size={{ xs: 12 }}>
                    <Stack>
                      <AmmunitionInformationTitle variant="subtitle1">
                        {t(EAmmunitionLabels.ammunitionDetailedInformation)}
                      </AmmunitionInformationTitle>
                    </Stack>
                  </Grid>
                  {showField('fireCycleId') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAmmunitionLabels.firecycleID)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(ammunitionDetail?.fireCycleId)}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  {showField('lotNumber') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAmmunitionLabels.lotNumber)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(ammunitionDetail?.lotNumber)}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  {showField('batchNumber') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAmmunitionLabels.batchNumber)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(ammunitionDetail?.batchNumber)}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  {showField('assetId') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAmmunitionLabels.assetId)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(ammunitionDetail?.assetId)}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  {showField('state') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAmmunitionLabels.ammunitionState)}
                        </Typography>
                        <Typography variant="body2">
                          <Chips
                            text={findInDictionary(ammunitionDetail?.stateUniqueId, state)}
                            variant={
                              renderDataColorByValues(
                                ammunitionDetail.activityUniqueId,
                                ammunitionDetail.legalityUniqueId,
                                ammunitionDetail.stateUniqueId,
                              ).stateField
                            }
                          />
                        </Typography>
                      </Stack>
                    </Grid>
                  )}

                  {showField('ownerName') && ammunitionDetail.ownerType && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAmmunitionLabels.currentOwner)}
                        </Typography>
                        <Typography variant="body2">
                          <StyledLegalLink
                            legalId={ammunitionDetail.ownerId}
                            legalType={getLegalEntityType(ammunitionDetail.ownerType)}
                            labelText={ammunitionDetail.ownerName}
                            legalVisibilityUniqueId={ammunitionDetail.ownerVisibilityUniqueId}
                          />
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  {showField('keeperName') && ammunitionDetail.keeperType && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAmmunitionLabels.currentKeeper)}
                        </Typography>
                        <Typography variant="body2">
                          <Typography variant="body2">
                            <StyledLegalLink
                              legalId={ammunitionDetail.keeperId}
                              legalType={getLegalEntityType(ammunitionDetail.keeperType)}
                              labelText={ammunitionDetail.keeperName}
                              legalVisibilityUniqueId={
                                ammunitionDetail.keeperVisibilityUniqueId
                              }
                            />
                          </Typography>
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  {showField('productionDate') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAmmunitionLabels.productionDate)}
                        </Typography>
                        <Typography variant="body2">
                          {ammunitionDetail?.productionDate
                            ? formatDate(ammunitionDetail?.productionDate, shortDateFormat)
                            : '-'}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  {showField('ammunitionExpiryDate') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAmmunitionLabels.expirationDate)}
                        </Typography>
                        <Typography variant="body2">
                          {ammunitionDetail?.ammunitionExpiryDate
                            ? formatDate(
                                ammunitionDetail?.ammunitionExpiryDate,
                                shortDateFormat,
                              )
                            : '-'}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  {showField('exhibitNumber') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAmmunitionLabels.exhibitNumber)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(ammunitionDetail?.exhibitNumber)}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  {ammunitionDetail?.customFields?.map((customField) => (
                    <Grid size={{ xs: 6, lg: 3 }} key={customField.customFieldUniqueId}>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {customField?.translations?.filter(
                          (translation) =>
                            selectedLanguage?.uniqueId === translation.languageUniqueId,
                        )?.[0]?.name ||
                          customField?.translations?.filter(
                            (translation) =>
                              defaultLanguage?.uniqueId === translation.languageUniqueId,
                          )?.[0]?.name}
                      </Typography>
                      <Typography variant="body2">
                        {renderCustomFieldData(customField)}
                      </Typography>
                    </Grid>
                  ))}

                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAmmunitionLabels.headstampYear)}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(ammunitionDetail?.headstampYear)}
                      </Typography>
                    </Stack>
                  </Grid>
                </AmmunitionInformationContainer>
                <AmmunitionInformationContainer size={{ xs: 12 }} container borderHidden>
                  {ammunitionDetail.markings && showMarkings && (
                    <>
                      <Grid size={{ xs: 12 }}>
                        <Stack>
                          <AmmunitionInformationTitle variant="subtitle1">
                            {t(EAmmunitionLabels.ammunitionMarkings)}
                          </AmmunitionInformationTitle>
                        </Stack>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EAmmunitionLabels.importMarking)}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(
                              ammunitionDetail.markings.find(
                                (marking) => marking.markingType === EMarkingTypes.IMPORT,
                              )?.marking,
                            )}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EAmmunitionLabels.appliedMarking)}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(
                              ammunitionDetail.markings.find(
                                (marking) => marking.markingType === EMarkingTypes.APPLIED,
                              )?.marking,
                            )}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EAmmunitionLabels.deactivationMarking)}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(
                              ammunitionDetail.markings.find(
                                (marking) =>
                                  marking.markingType === EMarkingTypes.DEACTIVATION,
                              )?.marking,
                            )}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EAmmunitionLabels.exportMarking)}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(
                              ammunitionDetail.markings.find(
                                (marking) => marking.markingType === EMarkingTypes.EXPORT,
                              )?.marking,
                            )}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EAmmunitionLabels.surplusSaleMarking)}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(
                              ammunitionDetail.markings.find(
                                (marking) =>
                                  marking.markingType === EMarkingTypes.SURPLUS_SALE,
                              )?.marking,
                            )}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EAmmunitionLabels.yearOfTransfer)}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(ammunitionDetail.yearOfTransfer)}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EAmmunitionLabels.confiscationMarking)}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(
                              ammunitionDetail.markings.find(
                                (marking) =>
                                  marking.markingType === EMarkingTypes.CONFISCATION,
                              )?.marking,
                            )}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EAmmunitionLabels.yearOfConfiscation)}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(ammunitionDetail.yearOfConfiscation)}
                          </Typography>
                        </Stack>
                      </Grid>
                    </>
                  )}
                  {ammunitionDetail.markings && (
                    <Grid
                      container
                      size={{ xs: 12 }}
                      sx={{ justifyContent: 'flex-end', mt: 3 }}
                    >
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Button
                          fullWidth
                          label={
                            showMarkings
                              ? t('ancillaryDetails.hideAncillaryMarkings.button')
                              : t('ancillaryDetails.seeAncillaryMarkings.button')
                          }
                          variant={EButtonVariants.outlined}
                          size={EButtonSizes.small}
                          onClick={() => setShowMarkings(!showMarkings)}
                        />
                      </Grid>
                    </Grid>
                  )}
                </AmmunitionInformationContainer>
              </Grid>
            </Grid>
          </DetailsTemplate>
          <Grid size={{ xs: 12 }}>
            <Stack sx={{ pb: 2, pt: 2 }}>
              {nextPageAlert && (
                <Box sx={{ marginBottom: '16px' }}>
                  <Alert text={nextPageAlert.text} variant={nextPageAlert.variant} />
                </Box>
              )}
              {ancillaryAlert && (
                <Box sx={{ marginBottom: '16px' }}>
                  <Alert text={ancillaryAlert.text} variant={ancillaryAlert.variant} />
                </Box>
              )}
              {transactionAlert && (
                <Box sx={{ marginBottom: '16px' }}>
                  <Alert text={transactionAlert.text} variant={transactionAlert.variant} />
                </Box>
              )}
              {hasPermissions([EPermission.VIEW_LIMITED_ANCILLARIES]) && (
                <Button
                  label={
                    isAllExpanded
                      ? t('ammunitionDetails.collapseAll.button')
                      : t('ammunitionDetails.expandAll.button')
                  }
                  onClick={() => setIsAllExpanded((prev) => !prev)}
                  variant={EButtonVariants.outlined}
                  sx={{ width: '150px', alignSelf: 'flex-end', mt: 2 }}
                />
              )}
            </Stack>
          </Grid>
        </Grid>
      )}
      {hasPermissions(
        [EPermission.VIEW_LIMITED_ANCILLARIES, EPermission.VIEW_ALL_ANCILLARIES],
        true,
      ) && (
        <Accordion
          title={t('ammunitionDetails.transactions.header')}
          name="transactions"
          key="transactions"
          expanded={isAllExpanded}
        >
          <AmmunitionTransactions
            inGroup={
              !!ammunitionDetail?.groupName &&
              ![EFirearmStateId.EXPORTED].includes(
                ammunitionDetail?.stateUniqueId as EFirearmStateId,
              )
            }
            ownerId={ammunitionDetail?.ownerId}
            ownerVisibilityUniqueId={ammunitionDetail?.ownerVisibilityUniqueId}
            keeperId={ammunitionDetail?.keeperId}
            keeperVisibilityUniqueId={ammunitionDetail?.keeperVisibilityUniqueId}
            stateUniqueId={ammunitionDetail?.stateUniqueId}
            hideManagement={!licenceAuthorityValid}
          />
        </Accordion>
      )}
      {hasPermissions([EPermission.MANAGE_COMMENTS_ANCILLARY]) && (
        <Accordion
          title={t('ammunitionDetails.comments.header')}
          name="comments"
          key="comments"
          expanded={isAllExpanded}
        >
          <AmmunitionComments id={id} hideManagement={!licenceAuthorityValid} />
        </Accordion>
      )}
      {hasPermissions([EPermission.MANAGE_ATTACHMENT_ANCILLARY]) && (
        <Accordion
          title={t('ammunitionDetails.attachments.header')}
          name="attachments"
          key="attachments"
          expanded={isAllExpanded}
        >
          <AmmunitionAttachments idAncillary={id} hideManagement={!licenceAuthorityValid} />
        </Accordion>
      )}
    </div>
  );
};

export default DetailAmmunition;
